import {
  IBaseApiResponse,
  TrainingStatus,
  LevelAssessmentType,
} from '@taleemabad/shared';
import { CourseRecord, DownloadInfo, TrainingRecord } from '..';
import { LargeNumberLike } from 'crypto';

interface IGetTeachersBaseResponse {
  test: string;
}

export type IGetTeachersApiResponse =
  IBaseApiResponse<IGetTeachersBaseResponse>;

export enum CourseType {
  General = 'general',
  Subject = 'subject',
}

export enum QuestionType {
  Mcq = 'mcq',
  Msq = 'msq',
  OpenEnded = 'open-ended',
  Poll = 'poll',
}

export enum GradeGroupType {
  Primary = 'primary',
  Early = 'early',
}
export interface ICourseLevel {
  id: number;
  label?: string;
  value?: number;
  name: string;
  description: string;
  order: number;
  passingScore: number;
  maxAttempts: number;
  timeLimit?: number | null;
  vendor?: string; // e.g., 'NIETE', 'OXBRIDGE', 'BEACONHOUSE'
}
export interface ICourse {
  title: string;
  id: number;
  index: number;
  description: string;
  keywords: string[];
  thumbnailUrl: string;
  level?: ICourseLevel;
  // the thumbnail is stored only in the local database
  // is not returned from the backend.
  thumbnailBase64?: string;
  thumbnailUrlDownloadAttempts?: number;
  timeDuration: number;
  trainingsCount: number;
  subject: number | null;
  gradeGroup?: number | null;
  gradeGroupType?: string | null;
  courseCompletionPercentage: number;
  courseStatus: ProgressStatus | null;
  type: ICourseType;
  downloadStatus?: string;
  downloadProgress?: number;
  downloadErrorCode?: string;
  profileId?: string;
  profileType?: string;
}

export enum ICourseType {
  CharacterDevelopment = 'CHARACTER_EDUCATION',
  EarlyYears = 'EARLY_YEARS',
  Fln = 'FLN',
  General = 'GENERAL',
  Subject = 'SUBJECT',
}

export enum MediaType {
  Image = 'image',
  Video = 'video',
  Audio = 'audio',
  Pdf = 'pdf',
}
export interface IMediaAsset {
  id: number;
  created: string;
  modified: string;
  uuid: string;
  name: string;
  type: MediaType;
  category: string[];
  url: string;
  tempUrl: string;
  hlsVideoMainManifestUrl: string;
  description: string;
  status: string;
  isActive: boolean;
  download?: DownloadInfo;
}

export interface ITraining {
  id: number;
  index: number;
  title: string;
  description: string;
  mediaAsset?: IMediaAsset;
  isGrandAssessment: boolean;
  course: number;
  trainingStatus: null | ProgressStatus;
  assessment: null | IAssessment;
  content?: string;
  profileId?: string;
  profileType?: string;
}

export interface ITrainingQuestion {
  id: number;
  index: number;
  type: QuestionType;
  questionStatement: string;
  options: string[];
  answers: number[];
  hint: string;
  hints: string[];
  training: number;
  statementMediaAsset?: IMediaAsset;
  bloomLevel?: BloomLevel;
  vendor?: string;
}

export enum ProgressStatus {
  InProgress = 'IN_PROGRESS',
  Pending = 'PENDING',
  Completed = 'COMPLETED',
}

export enum QuizPopupText {
  Diagnostic = 'Good job! You finished the practice test. You can now proceed to Trainings!',
  LevelExamPassed = 'You scored more than 80% and have successfully passed. Your training certificate is ready.',
  LevelExamFailed = 'You need at least 80% to pass. You can try again after 24 hours.',
  QuizPassed = 'Nice Job! You scored good in quiz.',
  QuizFailed = 'Retake quiz to improve your score.',
  QuizPassedButRequiresCoachAssessment = 'Congratulations! You have successfully completed  this Level. You will receive your certificate after the practicum.',
}
export enum LevelExamPassedPopupText {
  RequiresCoachAssessment = 'Congratulations! You have successfully completed  this Level. You will receive your certificate after the practicum.',
  LevelCertficateText = ' You can either reattempt the exam or download your completion certificate',
}
export enum BloomLevel {
  Remember = 'remember',
  Understand = 'understand',
  Apply = 'apply',
  Analyze = 'analyze',
  Evaluate = 'evaluate',
  Create = 'create',
  Receiving = 'receiving',
  Responding = 'responding',
  Valuing = 'valuing',
  Organization = 'organization',
  Characterization = 'characterization',
  Perception = 'perception',
  Set = 'set',
  GuidedResponse = 'guided_response',
  Mechanism = 'mechanism',
  ComplexOvertResponse = 'complex_overt_response',
  Adaptation = 'adaptation',
}

export interface ITrainingStatus {
  training: number;
  courseId: number;
  status: ProgressStatus;
  participant?: string;
}

export interface ISubmission {
  training: number | null;
  answer: string;
  question: number;
  isCorrect: boolean;
  questionType: string;
  questionCopy: ITrainingQuestion;
  score: number;
  grandQuiz: number | null;
  participant?: string;
  feedback?: string;
}

export interface IAssessment {
  training?: number | null;
  grandQuiz?: number | null;
  score: number;
  totalScore: number;
  isPassed?: boolean;
  participant?: string;
  profileId?: string;
}

export interface TrainingCourseProgressInfo {
  cta: string | null;
  progressText: string | null;
  allTrainingsCompleted: boolean;
  training: TrainingRecord | null;
  course: CourseRecord | null;
}

export interface TrainingLevelInfo {
  id: number;
  name: string;
  numberOfCourse: number;
  progressPercentage: number;
  onClick: () => void;
  currentStatus: TrainingStatus;
  onAttemptExamClick?: () => void;
  disableStartExam?: boolean;
  primaryButtonText?: string;
  dataCy?: string;
  isCertified?: boolean;
  onViewCertificate?: () => void;
}
export interface IGrandQuizzes {
  description: string;
  id: number;
  instructions: string;
  level: number;
  mediaAsset: IMediaAsset | null;
  status: string;
  title: string;
  assessments: IGrandQuizStatus[];
  type: LevelAssessmentType;
  submissions: ISubmission[];
  profileId?: string;
  profileType?: string;
}
export interface ILevels {
  id: number;
  name: string;
  description: string;
  order: number;
  passingScore: number;
  maxAttempts: number;
  timeLimit?: number | null;
  vendor?: string; // e.g., 'NIETE', 'OXBRIDGE', 'BEACONHOUSE'
  profileId?: string;
  profileType?: string;
}
export interface IGrandQuizStatus {
  attemptNumber: number;
  score: number;
  id: number;
  isPassed: boolean;
  lastAttemptedAt: string;
  grandQuiz: number;
  created?: string;
}
